/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState } from "react";
import { Modal, Loading } from "components/common";
import { useHistory } from "react-router-dom";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import closeIcon from "assets/close.svg";
import { boostProductTierConfig } from "../consts";
import {
  GET_BOOST_UPCOMING_INVOICE,
  UPDATE_BOOST_SUBSCRIPTION_TIER,
  CHECK_PROMO_CODE_VALID,
} from "../mutations";
import "../BoostLandingStyle.css";

const formatPromoCode = (str) => str?.trim().toUpperCase();
const formatStripeAmount = (int) => `$${(int / 100).toFixed(2)}`;

const ConfirmModal = ({
  selectedTerm,
  selectedTier,
  confirmModalOpen,
  setConfirmModalOpen,
  belowMd,
}) => {
  const tierConfig = boostProductTierConfig[selectedTerm]?.[selectedTier];
  const { price = 0 } = tierConfig || {};
  const perMonthPrice = selectedTerm === "yearly" ? (price / 12).toFixed(2) : price;
  const [confirmError, setConfirmError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState("");
  const [promoCodeMessage, setPromoCodeMessage] = useState("");
  const history = useHistory();

  const {
    loading: getInvoiceLoading,
    error: getInvoiceError,
    data: invoiceData,
    refetch: refetchInvoice,
  } = useQuery(GET_BOOST_UPCOMING_INVOICE, {
    variables: {
      productTier: selectedTier,
      productTerm: selectedTerm,
    },
  });

  const [
    updateCreditBoostSubscriptionTier,
    { loading: updatingTier },
  ] = useMutation(UPDATE_BOOST_SUBSCRIPTION_TIER);

  const [
    checkPromoCodeValid,
    { loading: checkingCode },
  ] = useLazyQuery(CHECK_PROMO_CODE_VALID, {
    fetchPolicy: "cache-and-network",
    onCompleted: async (res) => {
      // also has credit, type
      const { valid, reason } = res?.checkPromoCodeValid || {};
      if (!valid) {
        const errorMessage = reason || "Cannot connect to server";
        setPromoCodeMessage(errorMessage);
        return;
      }
      const formattedCode = formatPromoCode(promoCode);
      setPromoCodeMessage(`Promotion Code Applied: ${formattedCode}`);
      setPromoCodeApplied(formattedCode);
      refetchInvoice({ promoCode: formattedCode });
    },
    onError: (err) => {
      const { message, displayMessage } = err.graphQLErrors?.[0] || {};
      setPromoCodeMessage(displayMessage || message || "Error updating subscription");
    },
  });

  // helper when user confirm upgrade in modal
  const handleBoostTierConfirmed = async () => {
    if (!selectedTier) {
      return;
    }

    if (updatingTier || confirmError) {
      return;
    }

    let updateTierRes;
    try {
      updateTierRes = await updateCreditBoostSubscriptionTier({
        variables: {
          productTier: selectedTier,
          productTerm: selectedTerm,
          promoCode: promoCodeApplied,
        },
      });
    } catch (err) {
      setConfirmError(err.graphQLErrors?.[0]?.message || "Error updating subscription");
      return;
    }

    const { status, message } = updateTierRes?.data?.updateCreditBoostSubscriptionTier || {};
    if (status !== "SUCCESS") {
      const errorMessage = message || "Cannot connect to server";
      setConfirmError(errorMessage);
      return;
    }

    setConfirmModalOpen(false);
    history.push("/boost");
  };

  const handlePromoCodeEntry = (e) => {
    setPromoCode(e.target.value?.trim());
  };

  const checkCode = () => {
    setPromoCodeApplied("");
    if (!promoCode) {
      return;
    }
    if (updatingTier || checkingCode) {
      return;
    }
    const formattedCode = formatPromoCode(promoCode);
    checkPromoCodeValid({
      variables: {
        productTier: selectedTier,
        productTerm: selectedTerm,
        promoCode: formattedCode,
        isUpgrade: true,
      },
    });
  };

  const displayInvoiceError = () => {
    const { message, displayMessage } = getInvoiceError?.graphQLErrors[0] || {};
    return displayMessage || message || "Error getting invoice preview";
  };

  const { amountDue } = invoiceData?.getUserBoostUpcomingInvoiceWithUpdate || {};
  const formattedAmountDue = amountDue ? formatStripeAmount(amountDue) : "";

  if (getInvoiceLoading) {
    return <Loading boost />;
  }

  const closeModal = () => setConfirmModalOpen(false);

  return (
    <Modal
      isOpen={confirmModalOpen}
      close={closeModal}
      hasCrossContainer={false}
      options={{ showCloseButton: false }}
    >
      <div
        className="bg-white text-black rounded-xl p-8 text-left mx-6"
        style={{ maxWidth: 564, minHeight: 454, letterSpacing: 0.1 }}
      >
        <div className="flex justify-between">
          <h1 className="text-2xl font-bold mr-2">{confirmError ? "Upgrade Unsuccessful" : "Confirm Subscription Upgrade"}</h1>
          <button type="button" onClick={closeModal}>
            <img src={closeIcon} alt="close"/>
          </button>
        </div>
        {/* Message if no error */}
        {!confirmError && (
          <div className="mt-4 flex flex-col">
            <div className="">
              You have selected the {tierConfig?.name} subscription at <span className="font-bold">${tierConfig?.price}</span> {tierConfig?.billingPeriod?.toLowerCase()}
              {selectedTerm === "yearly" && (
                <span> (${perMonthPrice} per month)</span>
              )}
              .
            </div>
            {formattedAmountDue ? (
              <>
                <div className="mt-4">Your first payment amount will be <span className="font-bold">{formattedAmountDue}</span> applied to your current payment method.</div>
                <div className="mt-4">Get ready to boost your credit with TomoBoost!</div>
              </>
            ) : (
              (getInvoiceError && <div className="mt-4 text-red-500">{displayInvoiceError()}</div>)
            )}
            <div className="mt-4 font-bold text-sm">Have a promo code?</div>
            <div className="flex flex-row">
              <input
                id="promotionalInput"
                disabled={checkingCode || updatingTier}
                className="placeholder-gray-600 text-black px-2 outline-none bg-white rounded-md rounded-r-none border border-neutral-500 border-r-0 w-full"
                type="text"
                style={{
                  outline: "none",
                  paddingTop: 7,
                  paddingBottom: 7,
                  minWidth: 200,
                }}
                placeholder="Enter promo code"
                value={promoCode}
                onChange={handlePromoCodeEntry}
              />
              <button
                type="button"
                disabled={checkingCode || updatingTier}
                id="submit"
                onClick={checkCode}
                className="text-white px-4 focus:outline-none rounded-md rounded-l-none bg-boost-primary-500 hover:bg-boost-primary-400 active:bg-boost-primary-600 border border-neutral-500"
              >
                <span id="promotion-submit" className="whitespace-nowrap font-medium">
                  {checkingCode ? "Checking" : "Apply"}
                </span>
              </button>
            </div>
            {promoCodeMessage && <div className={`mt-1 text-sm ${promoCodeApplied ? "text-black" : "text-red-500"}`}>
              {promoCodeMessage}
            </div>}
            <p className="text-xs mt-4">
              TomoBoost does not guarantee an increase in credit score.
              Individual results may vary. Subscription payments are
              non-refundable. You may cancel upcoming payments by contacting
              customer support.
            </p>
            <div className={`mt-4 pt-4 flex gap-4 ${belowMd ? "flex-col" : "flex-row-reverse"} border-t border-neutral-400`}>
              <button
                type="button"
                onClick={handleBoostTierConfirmed}
                className="px-4 py-[6px] text-white bg-boost-primary-500 hover:bg-boost-primary-400 active:bg-boost-primary-600 font-medium hover:font-medium text-center rounded-md flex-shrink-0"
                disabled={updatingTier || confirmError}
              >
                Confirm Upgrade
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-[6px] bg-white border border-neutral-400 hover:bg-neutral-200 active:bg-neutral-300 font-medium hover:font-medium text-center rounded-md flex-shrink-0"
                disabled={updatingTier}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* Message if there is an error */}
        {confirmError && (
          <div className="mt-4">
            <p className="">
              Unfortunately, your subscription upgrade payment has failed, please try again and/or contact your bank. If this problem persists, please reach out to Customer Support at{" "}
              <a
                className="underline"
                href="mailto:boost@tomocredit.com"
              >
                boost@tomocredit.com
              </a>
            </p>
            {confirmError && (
              <div className="mt-4">
                <span className="font-bold">Payment Failure Reason: </span>
                {confirmError}
              </div>
            )}
            <div
              className="mt-2 underline cursor-pointer"
              onClick={() => history.push(`/settings?section=payment-method`)}
            >
              Update your payment method here if needed.
            </div>
            <div className="text-xs mt-20">
              TomoBoost does not guarantee an increase in credit score.
              Individual results may vary. Subscription payments are
              non-refundable. You may cancel upcoming payments by contacting
              customer support.
            </div>
            <div className={`mt-4 pt-4 flex ${belowMd ? "mt-4 flex-col" : "flex-row justify-end"} border-t border-neutral-400`}>
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-[6px] text-white bg-boost-primary-500 hover:bg-boost-primary-400 active:bg-boost-primary-600 font-medium hover:font-medium text-center rounded-md flex-shrink-0"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ConfirmModal;